import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const stars: { x: number; y: number; r: number; a: number; speed: number }[] = [];
    for (let i = 0; i < 200; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.5 + 0.2,
        a: Math.random(),
        speed: Math.random() * 0.3 + 0.05,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((s) => {
        s.a += s.speed * 0.01;
        if (s.a > 1) s.a = 0;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${s.a})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: '50%', labelKey: 'hero.stat1' },
    { value: '10K€', labelKey: 'hero.stat2' },
    { value: '2025', labelKey: 'hero.stat3' },
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050508]">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/765da1de00bfa06b68be07b967a18bc3.jpg')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050508]/70 via-[#050508]/50 to-[#050508]" />
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      <div className="relative z-10 w-full text-center px-4 md:px-6 max-w-5xl mx-auto pt-20">
        <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-['Rajdhani'] uppercase tracking-widest px-4 py-2 rounded-full mb-6 md:mb-8 animate-pulse">
          <i className="ri-planet-line"></i>
          {t('hero.badge')}
        </div>

        <h1 className="font-['Orbitron'] text-white font-black text-4xl sm:text-5xl md:text-7xl leading-tight mb-4 md:mb-6 tracking-tight">
          {t('hero.title1')}
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
            {t('hero.title2')}
          </span>
        </h1>

        <p
          className="text-gray-300 text-base md:text-xl max-w-2xl mx-auto mb-8 md:mb-12 leading-relaxed font-['Rajdhani']"
          dangerouslySetInnerHTML={{ __html: t('hero.desc') }}
        />

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
          <button
            onClick={() => scrollTo('#contracts')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black text-sm md:text-base px-7 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-['Orbitron'] tracking-wider shadow-lg shadow-amber-500/30"
          >
            <i className="ri-rocket-2-line"></i>
            {t('hero.cta.primary')}
          </button>
          <button
            onClick={() => scrollTo('#about')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 border border-amber-500/40 hover:border-amber-400 text-amber-400 hover:text-amber-300 font-bold text-sm md:text-base px-7 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-['Rajdhani'] uppercase tracking-wider"
          >
            <i className="ri-information-line"></i>
            {t('hero.cta.secondary')}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 md:gap-6 mt-14 md:mt-20 max-w-xs sm:max-w-sm md:max-w-2xl mx-auto">
          {stats.map((s) => (
            <div key={s.labelKey} className="text-center">
              <div className="font-['Orbitron'] text-amber-400 font-black text-xl md:text-3xl">{s.value}</div>
              <div className="text-gray-400 text-[10px] md:text-xs font-['Rajdhani'] uppercase tracking-widest mt-1">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 animate-bounce">
        <div className="text-gray-500 text-xs font-['Rajdhani'] uppercase tracking-widest">Scroll</div>
        <i className="ri-arrow-down-line text-amber-500/60 text-xl"></i>
      </div>
    </section>
  );
}